import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "react-scroll-to-top";
import Loader from "react-js-loader";
import { useTheme } from '@mui/material/styles';
import moment from "moment";
import {
  Avatar,
  Button,
  Container,
  Divider,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Modal,
  Box,
} from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';

import idSample from '../../assets/img/sample-id.jpg';
import {
  getBookingDetail,
  approveBooking,
  declineBooking
} from "../../redux/actions/book";
import config from "../../config/config";
import "./style.css";
import ViewProfilePhoto from "../../components/layout/ViewProfilePhoto";

const confirmModalText = [
  'You have approved this booking and can now onboard the client.',
  'This booking has been declined.'
]

const BookDetailPage = () => {
  const { bookId } = useParams();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [openDecline, setOpenDecline] = useState(false);
  const [fullImageUrl, setFullImageUrl] = useState();
  const [visibleConfirmModal, setVisibleConfirmModal] = useState(0);
  const [showViewProfilePhoto, setShowViewProfilePhoto] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();

  const { loading, bookingDetailData, emailNotification, isSuper } = useSelector(state => ({
    loading: state.book.loading,
    bookingDetailData: state.book.bookingDetailData,
    emailNotification: state.book.emailNotification,
    isSuper: state.auth.isSuper,
  }));

  useEffect(() => {
    dispatch(getBookingDetail(bookId));
  }, []);

  function getDurationText(duration) {
    if (duration == null) {
      return '';
    }
    return duration < 1 ? 'Custom' : duration > 1 ? duration + ' Hours' : duration + ' Hour';
  }

  const handleClose = () => {
    setOpen(false);
    setOpenDecline(false);
  }

  const handleApprove = async () => {
    const { result } = await dispatch(approveBooking(bookId));
    if (result) {
      handleClose();
      setVisibleConfirmModal(1);
    }
  }

  const handleDecline = async () => {
    const { result } = await dispatch(declineBooking(bookId));
    if (result) {
      handleClose();
      setVisibleConfirmModal(2);
    }
  }

  const onCloseConfirmModal = () => setVisibleConfirmModal(0);
  const onCloseViewProfilePhoto = () => setShowViewProfilePhoto(false);

  const onViewProfilePhoto = (url) => {
    setFullImageUrl(url);
    setShowViewProfilePhoto(true);
  }

  return (
    <Container maxWidth="lg">
      <Grid
        item
        className="page-title"
        container
        justifyContent='space-between'
        sx={{ my: { sm: 2, md: 5 }, marginInline: 'auto' }}
      >
        <Grid item xs={12} sm={6}>
          <Typography sx={{ color: '#fff', fontSize: '1.5rem', fontWeight: 600 }}>Booking Details</Typography>
          <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 500, mt: '3px', opacity: 0.75 }}>You received an {bookingDetailData?.bookingType} booking request from {bookingDetailData?.fullName} on {moment(bookingDetailData?.createdAt).format('DD MMM YYYY')}.</Typography>
        </Grid>
        {bookingDetailData?.status === 0 && bookingDetailData?.isExpired === false && (
          <Grid item container direction='row' justifyContent='flex-end' alignItems="center" xs={12} sm="auto" sx={{ my: 1 }}>
            <Button color="success" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, padding: '10px 20px', textTransform: 'none', color: '#fff' }} onClick={() => setOpen(true)}>Approve Booking</Button>
            <Button color="error" variant="contained" sx={{ marginInlineStart: 2, fontSize: '0.8rem', fontWeight: 600, padding: '10px 20px', textTransform: 'none' }} onClick={() => setOpenDecline(true)}>Decline Booking</Button>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={0}>
        <Grid item container lg={4}>
          <Container
            direction='column'
            sx={{
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d',
              width: { md: '350px' },
              minHeight: '300px',
              borderRadius: 2,
              paddingY: 3,
            }}
          >
            {loading ?
              <Grid marginTop="150px">
                <Loader type="bubble-loop" bgColor={"#222"} color={'#222'} size={100} />
              </Grid> :
              <>
                {bookingDetailData.selfiePhoto ?
                  <Box className="overlay" onClick={() => onViewProfilePhoto(`${config.SERVER_BASE_URL}/${bookingDetailData.selfiePhoto}`)}>
                    <Box
                      component="img"
                      src={`${config.SERVER_BASE_URL}/${bookingDetailData.selfiePhoto}`}
                      alt="avatar"
                      sx={{
                        display: 'flex',
                        width: '80%',
                        minHeight: '240px',
                        maxHeight: '360px',
                        objectFit: 'cover',
                        borderRadius: '10px',
                        margin: '20px auto auto',
                        mt: 3
                      }}
                    />
                    <Box component="div" className="overlay-layer">
                      <VisibilityIcon sx={{ color: '#fff' }} />
                    </Box>
                  </Box> :
                  <Avatar sx={{ width: '220px', height: '240px', borderRadius: '0', objectFit: 'contain', backgroundColor: 'tomato', fontSize: '4rem', fontWeight: 600, margin: 'auto', mt: 4 }} aria-label="noselfie">
                    {bookingDetailData?.client?.firstName[0]}
                  </Avatar>
                }
                <Typography sx={{ fontSize: '36px', fontWeight: 800, lineHeight: '1.0', maxWidth: '200px', margin: '28px 48px 0px', textAlign: 'left' }}>{bookingDetailData?.fullName}</Typography>
                <Grid sx={{ textAlign: 'left', margin: '10px 42px' }}>
                  {bookingDetailData?.status === 0 ?
                    bookingDetailData?.isExpired ?
                      <Box className="booking-detail-type" sx={{ color: '#F1416C', fontSize: '13px', backgroundColor: theme.palette.mode ==="light" ? "#fff5f8" : "#3a2434" }}>Expired Booking</Box> : 
                      <Box className="booking-detail-type" sx={{ color: '#FFC700', fontSize: '13px', backgroundColor: theme.palette.mode ==="light" ? "#FFF8DD" : "#392F28" }}>Unconfirmed Booking</Box> :
                    bookingDetailData?.status === 1
                      ? <Box className="booking-detail-type" sx={{ color: '#F1416C', fontSize: '13px', backgroundColor: theme.palette.mode ==="light" ? "#FFF5F8" : "#3A2434" }}>Declined Booking</Box>
                      : <Box className="booking-detail-type" sx={{ color: '#50CD89', fontSize: '13px', backgroundColor: theme.palette.mode === 'light' ? "#E8FFF3" : "#1C3238" }}>Approved Booking</Box>
                  }
                </Grid>
                <Divider sx={{ mt: 3 }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 700, mt: 3.5, marginInlineStart: 6 }}>Email</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#ffffff' }}>{bookingDetailData?.client?.email}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, mt: 3, marginInlineStart: 6 }}>Contact Number</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#ffffff' }}>{bookingDetailData?.client?.phone}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, mt: 3, marginInlineStart: 6 }}>Ocupation</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#ffffff' }}>{bookingDetailData?.client?.occupation}</Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, mt: 3, marginInlineStart: 6 }}>Preferred Communication</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#ffffff' }}>
                  {bookingDetailData?.preferredCommuncation?.email && 'Email '}
                  {bookingDetailData?.preferredCommuncation?.text && 'Text '}
                  {bookingDetailData?.preferredCommuncation?.phone && 'Phone '}
                </Typography>
                <Typography sx={{ fontSize: '14px', fontWeight: 700, mt: 3, marginInlineStart: 6 }}>How did you find me?</Typography>
                <Typography sx={{ fontSize: '14px', mt: 0.5, mb: 5, marginInlineStart: 6, color: theme.palette.mode === 'light' ? '#3f4254' : '#ffffff' }}>{bookingDetailData?.client?.searchEngine}</Typography>
              </>
            }
          </Container>
        </Grid>
        <Grid
          item
          container
          lg={8}
          xs
          sx={{
            minHeight: '300px',
            borderRadius: 2
          }}
        >
          <Container
            direction='column'
            sx={{
              padding: { xs: '24px !important', md: '60px !important'},
              backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1e1e2d',
              minHeight: '300px',
              borderRadius: 2
            }}>
            {loading ?
              <Grid marginTop="150px">
                <Loader type="bubble-loop" bgColor={"#2196f3"} color={'#2196f3'} size={100} />
              </Grid> :
              <>
                <Grid>
                  <Typography sx={{ fontWeight: 800, fontSize: '20px' }}>About The Booking</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>Basic details about the booking.</Typography>
                  <TableContainer sx={{ width: '100%', mt: 2 }}>
                    <Table aria-label="simple table">
                      <TableBody>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Appointment Type</TableCell>
                          <TableCell sx={{ paddingX: 0, fontSize: '14px', textTransform: 'capitalize', textAlign: { xs: 'right', md: 'left' } }}>{bookingDetailData?.bookingType}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Preferred Date</TableCell>
                          <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>{moment(bookingDetailData?.preferredDate).format('DD MMM YYYY')}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Preferred Time</TableCell>
                          <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>{moment(bookingDetailData?.preferredDate).format('LT')}</TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Duration</TableCell>
                          <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>{getDurationText(bookingDetailData?.duration)}</TableCell>
                        </TableRow>
                        {bookingDetailData?.bookingType !== 'Incall' && (
                          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Location</TableCell>
                            <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>{bookingDetailData?.location}</TableCell>
                          </TableRow>
                        )}
                        {bookingDetailData?.perkTitle && (
                          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Requested Perk</TableCell>
                            <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>
                              <Typography sx={{ fontWeight: '700' }}>{bookingDetailData?.perkTitle}</Typography>
                              <Typography sx={{ fontSize: '0.8rem', fontStyle: 'italic' }}>(I acknowledge that I meet the requirements for this perk.)</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid sx={{ mt: 5 }}>
                  <Typography sx={{ fontWeight: 800, mt: 7, fontSize: '20px' }}>Introduction</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>A personal message from the client.</Typography>
                  <Typography sx={{ mt: 2, fontSize: '14px', fontStyle: 'italic' }}>
                    {bookingDetailData.message}
                  </Typography>
                </Grid>
                <Grid sx={{ mt: 5 }}>
                  <Typography sx={{ fontWeight: 800, mt: 8, fontSize: '20px' }}>Verification</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>Showing all provided references.</Typography>
                  <TableContainer sx={{ width: '100%', mt: 2 }}>
                    <Table aria-label="simple table">
                      <TableBody>
						{bookingDetailData?.ref1 && (
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Reference #1</TableCell>
                          <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>{bookingDetailData?.ref1}</TableCell>
                        </TableRow>
						)}
						{bookingDetailData?.ref2 && (
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Reference #2</TableCell>
                          <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>{bookingDetailData?.ref2}</TableCell>
                        </TableRow>
						)}
                        {bookingDetailData?.linkedinProfile && (
                          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>LinkedIn Profile</TableCell>
                            <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>{bookingDetailData?.linkedinProfile}</TableCell>
                          </TableRow>
                        )}
                        {bookingDetailData?.companyWebsite && (
                          <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600 }}>Company Website</TableCell>
                            <TableCell sx={{ paddingX: 0, fontSize: '14px', textAlign: { xs: 'right', md: 'left' } }}>{bookingDetailData?.companyWebsite}</TableCell>
                          </TableRow>
                        )}
                        <Grid container sx={{ mt: 2, display: { md: 'none' } }}>
                          <Grid item xs={12} md="auto">
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, width: 200, color: theme.palette.mode ==="light" ? "#181c32" : "#fff" }}>ID Upload</Typography>
                          </Grid>
                          <Grid item xs sx={{ mt: { xs: 1 } }}>
                            {bookingDetailData?.idCard ?
                              <Box className="overlay" onClick={() => onViewProfilePhoto(`${config.SERVER_BASE_URL}/${bookingDetailData.idCard}`)}>
                                <Box
                                  component="img"
                                  src={`${config.SERVER_BASE_URL}/${bookingDetailData.idCard}`}
                                  alt="idCardImage"
                                  sx={{ width: '240px', objectFit: 'cover' }}
                                />
                                <Box component="div" className="overlay-layer">
                                  <VisibilityIcon sx={{ color: '#fff' }} />
                                </Box>
                              </Box> :
                              <Box component="img" src={idSample} style={{ width: '240px' }} alt="idCardImage" />
                            }
                          </Grid>
                        </Grid>
                        <TableRow sx={{ display: { xs: 'none', md: 'table-row' }, '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ width: '35%', paddingX: 0, fontSize: '14px', fontWeight: 600, verticalAlign: 'top' }}>ID Upload</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, display: 'flex' }}>
                            {bookingDetailData?.idCard ?
                              <Box className="overlay" onClick={() => onViewProfilePhoto(`${config.SERVER_BASE_URL}/${bookingDetailData.idCard}`)}>
                                <Box
                                  component="img"
                                  src={`${config.SERVER_BASE_URL}/${bookingDetailData.idCard}`}
                                  alt="idCardImage"
                                  sx={{ width: '300px', objectFit: 'cover' }}
                                />
                                <Box component="div" className="overlay-layer">
                                  <VisibilityIcon sx={{ color: '#fff' }} />
                                </Box>
                              </Box> :
                              <Box component="img" src={idSample} style={{ width: '300px' }} alt="idCardImage" />
                            }
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid sx={{ mt: 5 }}>
                  <Typography sx={{ fontWeight: 800, mt: 6, fontSize: '20px' }}>Session Track</Typography>
                  <Typography sx={{ fontSize: '0.8rem' }}>This booking has been recorded to originate from this IP address.</Typography>
                  <TableContainer sx={{ width: '100%', mt: 2 }}>
                    <Table aria-label="simple table">
                      <TableBody>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '0.7rem', fontWeight: 600 }}>LOCATION</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '0.7rem', fontWeight: 600, display: { xs: 'none' } }}>DEVICE</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '0.7rem', fontWeight: 600 }}>IP ADDRESS</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '0.7rem', fontWeight: 600 }}>DATE</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '0.7rem', fontWeight: 600 }}>TIME</TableCell>
                        </TableRow>
                        <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px' }}>
                            {bookingDetailData?.client_device_info?.countryInfo === "" ? "Hidden": bookingDetailData?.client_device_info?.countryInfo}
                          </TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px', display: { xs: 'none' } }}>
                            {bookingDetailData?.client_device_info?.browserInfo} - {bookingDetailData?.client_device_info?.systemInfo}
                          </TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px' }}>
                            {bookingDetailData?.client_device_info?.ipInfo === "" ? "Hidden": bookingDetailData?.client_device_info?.ipInfo}
                          </TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px' }}>{moment(bookingDetailData?.createdAt).format('ll')}</TableCell>
                          <TableCell align="left" sx={{ paddingX: 0, fontSize: '14px' }}>{moment(bookingDetailData?.createdAt).format('LT')}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </>
            }
          </Container>
        </Grid>
      </Grid>
      <ScrollToTop
        smooth
        color="#009EF7"
      />
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '88%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question" sx={{ borderColor: "#009EF7" }}>
              <Grid className="swal-icon-content" sx={{ fontSize: '3.5em', color: '#009EF7' }}>?</Grid>
            </Grid>
            <Typography sx={{ fontSize: '1.5rem', textAlign: 'center', fontWeight: 700, lineHeight: '1.2', mt: 3 }}>
              You are about to approve this booking.
            </Typography>
            <Typography id="modal-modal-description" sx={{ fontSize: '0.85rem', mt: 2, textAlign: 'center' }}>
              An email {emailNotification?.approveMailStatus? 'will' : 'WILL NOT'} be sent to the client to notify them. Do you wish to proceed?
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button color="success" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: "#fff" }} onClick={handleApprove}>Confirm</Button>
              <Button color="error" variant="contained" sx={{ marginInlineStart: 1, fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none' }} onClick={handleClose}>Cancel</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Modal
        open={openDecline}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '88%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: "#F1416C" }}>
              <Grid className="swal-x-content" sx={{ fontSize: '2.75em', color: '#F1416C' }}>&#x2715;</Grid>
            </Grid>
            <Typography sx={{ fontSize: '1.5rem', textAlign: 'center', fontWeight: 700, lineHeight: '1.2', mt: 3 }}>
              You are about to decline this booking.
            </Typography>
            <Typography id="modal-modal-description" sx={{ fontSize: '0.85rem', mt: 2, textAlign: 'center' }}>
              This action cannot be undone. An email {emailNotification?.declineMailStatus? 'will' : 'WILL NOT'} be sent to the client to notify them. Do you wish to proceed?
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button color="success" variant="contained" sx={{ fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: '#fff' }} onClick={handleDecline}>Confirm</Button>
              <Button color="error" variant="contained" sx={{ marginInlineStart: 1, fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none' }} onClick={handleClose}>Cancel</Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <Modal
        open={visibleConfirmModal > 0}
        onClose={onCloseConfirmModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 360,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Grid sx={{ width: '88%', margin: 'auto' }} className="text-center">
            <Grid container justifyContent='center' alignItems='center' className="swal-question swal-close" sx={{ borderColor: visibleConfirmModal === 1 ? "#50cd89" : '#f1416c' }}>
              {visibleConfirmModal === 1
                ? <Grid className="swal-x-content" sx={{ fontSize: '3em', color: '#50cd89' }}>&#10004;</Grid>
                : <Grid className="swal-x-content" sx={{ fontSize: '2.75em', color: '#f1416c' }}>&#x2715;</Grid>
              }
            </Grid>
            <Typography sx={{ fontSize: '1.8rem', textAlign: 'center', fontWeight: 700, lineHeight: '1.2', mt: 3 }}>
              Booking Status Updated
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', textAlign: 'center', fontWeight: 400, mt: 2 }}>
              {confirmModalText[visibleConfirmModal - 1]}
            </Typography>
            <Grid item container direction='row' justifyContent='center' alignItems='center' display="flex" sx={{ mt: 3 }}>
              <Button
                color="success"
                variant="contained"
                sx={{ fontSize: '0.8rem', fontWeight: 600, padding: '10px 30px', textTransform: 'none', color: '#fff' }}
                onClick={() => navigate(isSuper ? '/superadmin' : '/bookings')}
              >
                Back to Bookings
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <ViewProfilePhoto
        visible={showViewProfilePhoto}
        onClose={onCloseViewProfilePhoto}
        photoUrl={fullImageUrl}
      />
    </Container>
  );
}

export default BookDetailPage;
